import {
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { fetchOrder, type OrderResponseV2, ResourceType } from '@library/api-order'
import { queueTrackingEvent } from '@library/tracking-integration'
import {
  applyDashboardPromotionCode,
  fetchOrderSkipDiscount,
  restoreOrder,
  skipOrder,
  type DashboardExperienceOrderDeliveryDetailsDay,
  type DashboardExperienceOrderResponse,
  type DashboardExperienceOrderSkipDiscountResponse,
  type UpdateDashboardExperienceOrderRequest,
} from '@library/api-dashboard-experience'
import React, { useEffect, useState } from 'react'
import { browserHistory } from 'react-router'
import { useSWRConfig } from 'swr'
import { OrderTileActions } from './OrderTileSubcomponents/OrderTileActions'
import { OrderTileHeader } from './OrderTileSubcomponents/OrderTileHeader'
import { OrderTileRecipes } from './OrderTileSubcomponents/OrderTileRecipes'
import { ResponsiveManageDeliveryPanel } from './OrderTileSubcomponents/ResponsiveManageDeliveryPanel'
import { isSkipOrder } from './orderTileState'
import { SkipOrderDiscountModal } from '../SkipOrderDiscountModal'
import { useOrderDeliveryDetails } from '../../hooks/useOrderDeliveryDetails'
import { useOrderTracking } from '../../hooks/useOrderTracking'
import { useUpdateDashboardOrder } from '../../hooks/useUpdateDashboardOrder'
import { ApiKeys } from '../../configs'

const VIEW_PAUSE_DISCOUNT_REMINDER_OFFER_SCREEN = 'view_pause_discount_reminder_offer_screen'

type ViewPauseDiscountReminderOfferScreenTrackingEvent = {
  action: typeof VIEW_PAUSE_DISCOUNT_REMINDER_OFFER_SCREEN
  properties: {
    actionType: 'Order Cancel' | 'Order Skip' | 'Order Cancelled' | 'Order Skipped'
    order_id?: string
    delivery_day_id: string
    order_state: string
    cms_variation: string
    recovery_reasons: [string | null, DashboardExperienceOrderSkipDiscountResponse | null]
  }
}

const getDiscountReminderActionType = ({
  hasDiscount,
  isPendingOrder,
}: {
  hasDiscount: boolean
  isPendingOrder: boolean
}): ViewPauseDiscountReminderOfferScreenTrackingEvent['properties']['actionType'] => {
  if (isPendingOrder) {
    return hasDiscount ? 'Order Cancel' : 'Order Cancelled'
  }

  return hasDiscount ? 'Order Skip' : 'Order Skipped'
}

type OrderTileProps = {
  onDeliveryDateSaved?: (selectedDeliveryDate: string) => void
  onSkipSuccess?: (orderId: string) => void
  order: DashboardExperienceOrderResponse
}

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      const statusMatch = message.match(/status=(\d+)|responded with (\d+)/)
      const matchedStatus = statusMatch?.[1] ?? statusMatch?.[2]

      if (matchedStatus) {
        return Number(matchedStatus)
      }
    }
  }

  return undefined
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : JSON.stringify(error)

const OrderTileContainer = withCitrusTheme(Box, (theme) => [
  theme.responsiveColor(
    [Color.White, Color.White, Color.White, Color.ColdGrey_100],
    ['borderTopColor'],
  ),
  theme.responsiveStyle(
    ['0px', '0px', '0px', '8px'],
    ['borderTopLeftRadius', 'borderTopRightRadius'],
  ),
  {
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
])

export function OrderTile({ onDeliveryDateSaved, onSkipSuccess, order }: OrderTileProps) {
  const {
    trackGetHelpWithThisBoxClick,
    trackOrderExpandClick,
    trackChooseRecipesClick,
    trackEditRecipesClick,
    trackRestoreOrderClick,
    trackCancelOrderClick,
    trackSaveDeliveryDate,
    trackMyOrderClick,
  } = useOrderTracking()
  const { mutate } = useSWRConfig()
  const [displayedOrder, setDisplayedOrder] = useState(order)
  const [isManageDeliveryExpanded, setIsManageDeliveryExpanded] = useState(false)
  const [isRestoringOrder, setIsRestoringOrder] = useState(false)
  const [isApplyingSkipDiscount, setIsApplyingSkipDiscount] = useState(false)
  const [isSkippingOrder, setIsSkippingOrder] = useState(false)
  const [isFetchingSkipDiscount, setIsFetchingSkipDiscount] = useState(false)
  const [isSkipDiscountModalOpen, setIsSkipDiscountModalOpen] = useState(false)
  const [skipDiscount, setSkipDiscount] =
    useState<DashboardExperienceOrderSkipDiscountResponse | null>(null)
  const [hasApplySkipDiscountError, setHasApplySkipDiscountError] = useState(false)
  const [hasSaveDeliveryDateError, setHasSaveDeliveryDateError] = useState(false)
  const [hasSkipOrderError, setHasSkipOrderError] = useState(false)
  const { orderDeliveryDetails } = useOrderDeliveryDetails(
    displayedOrder.id,
    isManageDeliveryExpanded,
  )
  const { isUpdating, updateOrder } = useUpdateDashboardOrder()
  const isSkippedOrder = isSkipOrder(displayedOrder)
  const priceKind = typeof displayedOrder.price.kind === 'string' ? displayedOrder.price.kind : null
  const originalPrice =
    typeof displayedOrder.price.original === 'string' ? displayedOrder.price.original : null
  const currentPrice =
    typeof displayedOrder.price.amount === 'string' ? displayedOrder.price.amount : null

  useEffect(() => {
    setDisplayedOrder(order)
    setHasApplySkipDiscountError(false)
    setHasSaveDeliveryDateError(false)
    setHasSkipOrderError(false)
    setIsApplyingSkipDiscount(false)
    setIsRestoringOrder(false)
    setIsFetchingSkipDiscount(false)
    setIsSkipDiscountModalOpen(false)
    setSkipDiscount(null)
  }, [order])

  const handleGetHelpClick = () => {
    trackGetHelpWithThisBoxClick({
      orderId: displayedOrder.id,
    })
    browserHistory.push(`/get-help?orderId=${displayedOrder.id}`)
  }

  const handleManageDeliveryClick = () => {
    trackOrderExpandClick({
      order: displayedOrder,
    })
    setIsManageDeliveryExpanded((currentValue) => !currentValue)
  }

  const handleChooseRecipesClick = () => {
    if (displayedOrder.state === 'choose') {
      trackChooseRecipesClick({
        order: displayedOrder,
      })
      return
    }

    if (displayedOrder.state === 'edit') {
      trackEditRecipesClick({
        order: displayedOrder,
      })
    }
  }

  const handleTrackBoxClick = (url: string) => {
    trackMyOrderClick({
      order: displayedOrder,
    })
    window.open(url, '_blank', 'noopener noreferrer')
  }

  const handleRestoreOrderClick = async () => {
    if (isRestoringOrder) {
      return
    }

    const orderId = displayedOrder.id
    trackRestoreOrderClick({
      order: displayedOrder,
    })

    const restoreTelemetryContext = {
      page: 'MyHomePage',
      actionType: 'restoreOrder',
      orderId,
      orderState: displayedOrder.analytics.order_state,
      orderPhase: displayedOrder.analytics.order_phase,
      deliveryDayId: displayedOrder.analytics.delivery_day_id,
      deliverySlotId: displayedOrder.analytics.delivery_slot_id,
      deliveryDate: displayedOrder.analytics.delivery_date,
      periodId: displayedOrder.analytics.period_id,
      portionSize: displayedOrder.analytics.portion_size,
    }

    setIsRestoringOrder(true)

    try {
      await restoreOrder(orderId)
      datadogRum.addAction('my_home_restore_order_success', restoreTelemetryContext)
      datadogLogs.logger.info('MyHomePage restore order succeeded', {
        ...restoreTelemetryContext,
        restoreOutcome: 'success',
      })
      await mutate(ApiKeys.DashboardExperience)
    } catch (error) {
      const errorMessage = `Error restoring order in myHome ${JSON.stringify(error)}`
      const statusCode = getStatusCode(error)

      datadogLogs.logger.error('MyHomePage restore order failed', {
        ...restoreTelemetryContext,
        restoreOutcome: 'error',
        statusCode,
        errorMessage,
      })
      datadogRum.addError(error, {
        ...restoreTelemetryContext,
        statusCode,
        errorMessage,
      })
    } finally {
      setIsRestoringOrder(false)
    }
  }

  const handleSkipDiscountModalClose = () => {
    if (isSkippingOrder || isApplyingSkipDiscount) {
      return
    }

    setHasApplySkipDiscountError(false)
    setHasSkipOrderError(false)
    setIsSkipDiscountModalOpen(false)
    setSkipDiscount(null)
  }

  const handleConfirmSkipBoxClick = async () => {
    if (isSkippingOrder) {
      return
    }

    const orderId = displayedOrder.id
    setHasApplySkipDiscountError(false)
    const skipTelemetryContext = {
      page: 'MyHomePage',
      actionType: 'skipOrder',
      orderId,
      orderState: displayedOrder.analytics.order_state,
      orderPhase: displayedOrder.analytics.order_phase,
      deliveryDayId: displayedOrder.analytics.delivery_day_id,
      deliverySlotId: displayedOrder.analytics.delivery_slot_id,
      deliveryDate: displayedOrder.analytics.delivery_date,
      periodId: displayedOrder.analytics.period_id,
      portionSize: displayedOrder.analytics.portion_size,
    }

    setHasSkipOrderError(false)
    setIsSkippingOrder(true)

    try {
      await skipOrder(orderId)
      datadogRum.addAction('my_home_skip_order_success', skipTelemetryContext)
      datadogLogs.logger.info('MyHomePage skip order succeeded', {
        ...skipTelemetryContext,
        skipOutcome: 'success',
      })
      setDisplayedOrder((currentOrder) => ({
        ...currentOrder,
        help_cta: null,
        restore_cta: currentOrder.restore_cta ?? 'Restore order',
        state: 'skip',
        state_title: 'Skipped',
      }))
      onSkipSuccess?.(orderId)
      setHasSkipOrderError(false)
      await mutate(ApiKeys.DashboardExperience)
      setIsSkipDiscountModalOpen(false)
      setSkipDiscount(null)
    } catch (error) {
      const errorMessage = `Error skipping order in myHome ${JSON.stringify(error)}`
      const statusCode = getStatusCode(error)

      datadogLogs.logger.error('MyHomePage skip order failed', {
        ...skipTelemetryContext,
        skipOutcome: 'error',
        statusCode,
        errorMessage,
      })
      datadogRum.addError(error, {
        ...skipTelemetryContext,
        statusCode,
        errorMessage,
      })
      datadogRum.addAction('my_home_skip_order_api_error', {
        ...skipTelemetryContext,
        skipOutcome: 'error',
        statusCode,
      })
      setHasSkipOrderError(true)
    } finally {
      setIsSkippingOrder(false)
    }
  }

  const handleSkipBoxRequestClick = async () => {
    if (isSkippingOrder || isFetchingSkipDiscount) {
      return
    }

    const orderId = displayedOrder.id
    const skipDiscountTelemetryContext = {
      page: 'MyHomePage',
      actionType: 'fetchSkipDiscount',
      orderId,
      orderState: displayedOrder.analytics.order_state,
      orderPhase: displayedOrder.analytics.order_phase,
      deliveryDayId: displayedOrder.analytics.delivery_day_id,
      deliverySlotId: displayedOrder.analytics.delivery_slot_id,
      deliveryDate: displayedOrder.analytics.delivery_date,
      periodId: displayedOrder.analytics.period_id,
      portionSize: displayedOrder.analytics.portion_size,
    }

    trackCancelOrderClick({
      order: displayedOrder,
    })
    setHasApplySkipDiscountError(false)
    setHasSkipOrderError(false)
    setIsFetchingSkipDiscount(true)
    setSkipDiscount(null)

    try {
      const orderSkipDiscount = await fetchOrderSkipDiscount(orderId)

      if (orderSkipDiscount) {
        const offerTelemetryContext = {
          ...skipDiscountTelemetryContext,
          isExistingDiscount: orderSkipDiscount.isExistingDiscount,
          promoCode: orderSkipDiscount.promoCode,
        }

        datadogRum.addAction('my_home_order_skip_discount_offer_available', offerTelemetryContext)
        datadogLogs.logger.info('MyHomePage order skip discount offer available', {
          ...offerTelemetryContext,
          fetchSkipDiscountOutcome: 'offerAvailable',
        })
        setSkipDiscount(orderSkipDiscount)
        setIsSkipDiscountModalOpen(true)

        return
      }
      datadogRum.addAction(
        'my_home_order_skip_discount_offer_unavailable',
        skipDiscountTelemetryContext,
      )
      datadogLogs.logger.info('MyHomePage order skip discount offer unavailable', {
        ...skipDiscountTelemetryContext,
        fetchSkipDiscountOutcome: 'offerUnavailable',
      })
      trackSkipDiscountReminder(null)
    } catch (error) {
      const errorMessage = `Error fetching order skip discount in myHome ${getErrorMessage(error)}`
      const statusCode = getStatusCode(error)

      datadogLogs.logger.error('MyHomePage order skip discount lookup failed', {
        ...skipDiscountTelemetryContext,
        fetchSkipDiscountOutcome: 'error',
        statusCode,
        errorMessage,
      })
      datadogRum.addError(error, {
        ...skipDiscountTelemetryContext,
        statusCode,
        errorMessage,
      })
      datadogRum.addAction('my_home_order_skip_discount_api_error', {
        ...skipDiscountTelemetryContext,
        fetchSkipDiscountOutcome: 'error',
        statusCode,
      })
      // If the discount lookup is unavailable or malformed, keep the skip flow unblocked.
    } finally {
      setIsFetchingSkipDiscount(false)
    }

    await handleConfirmSkipBoxClick()
  }

  const trackSkipDiscountReminder = (
    skipDiscountOffer: DashboardExperienceOrderSkipDiscountResponse | null,
  ) => {
    const hasDiscount = Boolean(skipDiscountOffer)
    const isPendingOrder = displayedOrder.analytics.order_state === 'pending'
    const properties: ViewPauseDiscountReminderOfferScreenTrackingEvent['properties'] = {
      actionType: getDiscountReminderActionType({ hasDiscount, isPendingOrder }),
      delivery_day_id: displayedOrder.analytics.delivery_day_id,
      order_state: displayedOrder.analytics.order_state,
      cms_variation: 'homepage',
      recovery_reasons: [skipDiscountOffer?.subtitle ?? null, skipDiscountOffer],
    }

    if (hasDiscount || isPendingOrder) {
      properties.order_id = displayedOrder.analytics.order_id
    }

    queueTrackingEvent<ViewPauseDiscountReminderOfferScreenTrackingEvent>({
      action: VIEW_PAUSE_DISCOUNT_REMINDER_OFFER_SCREEN,
      properties,
    })
  }

  const handleSkipDiscountModalView = () => {
    if (!skipDiscount) {
      return
    }

    trackSkipDiscountReminder(skipDiscount)
  }

  const handleApplySkipDiscountClick = async () => {
    if (isApplyingSkipDiscount || isSkippingOrder) {
      return
    }

    if (skipDiscount?.isExistingDiscount) {
      browserHistory.push(`/menu/${displayedOrder.id}`)

      return
    }

    const promoCode = skipDiscount?.promoCode?.trim()

    if (!promoCode) {
      setHasApplySkipDiscountError(true)

      return
    }

    const orderId = displayedOrder.id
    const applyDiscountTelemetryContext = {
      page: 'MyHomePage',
      actionType: 'applySkipDiscount',
      orderId,
      orderState: displayedOrder.analytics.order_state,
      orderPhase: displayedOrder.analytics.order_phase,
      deliveryDayId: displayedOrder.analytics.delivery_day_id,
      deliverySlotId: displayedOrder.analytics.delivery_slot_id,
      deliveryDate: displayedOrder.analytics.delivery_date,
      periodId: displayedOrder.analytics.period_id,
      portionSize: displayedOrder.analytics.portion_size,
      promoCode,
    }

    setHasApplySkipDiscountError(false)
    setIsApplyingSkipDiscount(true)

    try {
      const applyPromotionCodeResponse = await applyDashboardPromotionCode({ promoCode })
      datadogRum.addAction('my_home_apply_skip_discount_success', {
        ...applyDiscountTelemetryContext,
        applyPromotionCodeStatus: applyPromotionCodeResponse.status,
      })
      datadogLogs.logger.info('MyHomePage apply skip discount succeeded', {
        ...applyDiscountTelemetryContext,
        applySkipDiscountOutcome: 'success',
        applyPromotionCodeStatus: applyPromotionCodeResponse.status,
      })
      await mutate(ApiKeys.DashboardExperience)
      setHasApplySkipDiscountError(false)
      setIsSkipDiscountModalOpen(false)
      setSkipDiscount(null)
    } catch (error) {
      const errorMessage = `Error applying skip discount in myHome ${getErrorMessage(error)}`
      const statusCode = getStatusCode(error)

      datadogLogs.logger.error('MyHomePage apply skip discount failed', {
        ...applyDiscountTelemetryContext,
        applySkipDiscountOutcome: 'error',
        statusCode,
        errorMessage,
      })
      datadogRum.addError(error, {
        ...applyDiscountTelemetryContext,
        statusCode,
        errorMessage,
      })
      datadogRum.addAction('my_home_apply_skip_discount_api_error', {
        ...applyDiscountTelemetryContext,
        applySkipDiscountOutcome: 'error',
        statusCode,
      })
      setHasApplySkipDiscountError(true)
    } finally {
      setIsApplyingSkipDiscount(false)
    }
  }

  const resolveDeliveryDayId = (
    selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay,
    selectedDeliveryDate: string,
  ): string => {
    const snakeCaseCoreDayId = (selectedDeliveryDay as { core_day_id?: string }).core_day_id
    const isCurrentOrderDeliveryDate =
      selectedDeliveryDate === displayedOrder.analytics.delivery_date

    return (
      selectedDeliveryDay.coreDayId ||
      snakeCaseCoreDayId ||
      (isCurrentOrderDeliveryDate ? displayedOrder.analytics.delivery_day_id : '') ||
      selectedDeliveryDay.id ||
      ''
    )
  }

  const resolveDeliverySlotId = (
    selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay,
    selectedDeliveryDate: string,
  ): string => {
    const isCurrentOrderDeliveryDate =
      selectedDeliveryDate === displayedOrder.analytics.delivery_date
    const selectedDeliverySlot =
      selectedDeliveryDay.slots.find((slot) => slot.isDefault) || selectedDeliveryDay.slots[0]

    if (!selectedDeliverySlot) {
      return isCurrentOrderDeliveryDate ? displayedOrder.analytics.delivery_slot_id : ''
    }

    const snakeCaseCoreSlotId = (selectedDeliverySlot as { core_slot_id?: string }).core_slot_id

    return (
      selectedDeliverySlot.coreSlotId ||
      snakeCaseCoreSlotId ||
      (isCurrentOrderDeliveryDate ? displayedOrder.analytics.delivery_slot_id : '') ||
      selectedDeliverySlot.id ||
      ''
    )
  }

  const resolveSelectedDeliverySlot = (
    selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay,
  ) => selectedDeliveryDay.slots.find((slot) => slot.isDefault) || selectedDeliveryDay.slots[0]

  const updateIncludedResource = <
    TResource extends {
      id: string
      type: string
      attributes?: Record<string, unknown>
    },
  >(
    included: TResource[],
    nextResource: TResource,
  ) => {
    const existingResourceIndex = included.findIndex(
      (resource) => resource.type === nextResource.type && resource.id === nextResource.id,
    )

    if (existingResourceIndex >= 0) {
      const nextIncluded = [...included]

      nextIncluded[existingResourceIndex] = {
        ...nextIncluded[existingResourceIndex],
        ...nextResource,
        attributes: {
          ...nextIncluded[existingResourceIndex].attributes,
          ...nextResource.attributes,
        },
      }

      return nextIncluded
    }

    const sameTypeResourceIndex = included.findIndex(
      (resource) => resource.type === nextResource.type,
    )

    if (sameTypeResourceIndex >= 0) {
      const nextIncluded = [...included]

      nextIncluded[sameTypeResourceIndex] = {
        ...nextIncluded[sameTypeResourceIndex],
        ...nextResource,
        attributes: {
          ...nextIncluded[sameTypeResourceIndex].attributes,
          ...nextResource.attributes,
        },
      }

      return nextIncluded
    }

    return [...included, nextResource]
  }

  const buildUpdateOrderRequestBody = ({
    fullOrder,
    selectedDeliveryDay,
    selectedDeliveryDate,
  }: {
    fullOrder: OrderResponseV2
    selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay
    selectedDeliveryDate: string
  }): UpdateDashboardExperienceOrderRequest => {
    const selectedDeliverySlot = resolveSelectedDeliverySlot(selectedDeliveryDay)
    const relationships = fullOrder.data.relationships ?? {}
    const existingDeliverySlot = relationships.delivery_slot?.data
    const resolvedDeliverySlotId = resolveDeliverySlotId(selectedDeliveryDay, selectedDeliveryDate)
    const resolvedDeliveryDayId = resolveDeliveryDayId(selectedDeliveryDay, selectedDeliveryDate)
    const resolvedDaySlotLeadTimeId =
      selectedDeliverySlot?.daySlotLeadTimeId || relationships.day_slot_lead_time?.data.id
    const includedWithDeliverySlot = updateIncludedResource(fullOrder.included, {
      id: resolvedDeliverySlotId,
      type: ResourceType.DeliverySlot,
      attributes: {
        core_id: selectedDeliverySlot?.coreSlotId || resolvedDeliverySlotId,
        delivery_slot_id: selectedDeliverySlot?.coreSlotId || resolvedDeliverySlotId,
        start: selectedDeliverySlot?.deliveryStartTime || '',
        end: selectedDeliverySlot?.deliveryEndTime || '',
      },
    })
    const includedWithDeliveryDay = updateIncludedResource(includedWithDeliverySlot, {
      id: resolvedDeliveryDayId,
      type: ResourceType.DeliveryDay,
      attributes: {
        date: `${selectedDeliveryDate}T00:00:00Z`,
      },
    })
    const updatedIncluded =
      resolvedDaySlotLeadTimeId === undefined
        ? includedWithDeliveryDay
        : updateIncludedResource(includedWithDeliveryDay, {
            id: resolvedDaySlotLeadTimeId,
            type: ResourceType.DaySlotLeadTime,
          })

    const updatedOrder: UpdateDashboardExperienceOrderRequest = {
      ...fullOrder,
      data: {
        ...fullOrder.data,
        relationships: {
          ...relationships,
          delivery_slot: {
            data: {
              id: resolvedDeliverySlotId,
              type: ResourceType.DeliverySlot,
              meta: {
                ...(existingDeliverySlot?.meta ?? {}),
                core_id: selectedDeliverySlot?.coreSlotId || resolvedDeliverySlotId,
              },
            },
          },
          delivery_day: {
            data: {
              id: resolvedDeliveryDayId,
              type: ResourceType.DeliveryDay,
            },
          },
          ...(resolvedDaySlotLeadTimeId
            ? {
                day_slot_lead_time: {
                  data: {
                    id: resolvedDaySlotLeadTimeId,
                    type: ResourceType.DaySlotLeadTime,
                  },
                },
              }
            : {}),
        },
      },
      included: updatedIncluded,
    }

    return updatedOrder
  }

  const handleSaveDeliveryDate = async ({ date }: { date: string; formattedDate: string }) => {
    if (!orderDeliveryDetails) {
      return
    }

    const selectedDeliveryDay = orderDeliveryDetails.available_delivery_days?.[date]

    if (!selectedDeliveryDay) {
      return
    }

    const orderId = displayedOrder.id
    const newDeliverySlotId = resolveDeliverySlotId(selectedDeliveryDay, date)
    const updateDeliveryDateTelemetryContext = {
      page: 'MyHomePage',
      actionType: 'updateDeliveryDate',
      orderId,
      orderState: displayedOrder.analytics.order_state,
      orderPhase: displayedOrder.analytics.order_phase,
      deliveryDayId: displayedOrder.analytics.delivery_day_id,
      deliverySlotId: displayedOrder.analytics.delivery_slot_id,
      deliveryDate: displayedOrder.analytics.delivery_date,
      selectedDeliveryDate: date,
      periodId: displayedOrder.analytics.period_id,
      portionSize: displayedOrder.analytics.portion_size,
    }

    setHasSaveDeliveryDateError(false)

    try {
      const fullOrder = await fetchOrder(orderId)

      await updateOrder({
        orderId,
        body: buildUpdateOrderRequestBody({
          fullOrder,
          selectedDeliveryDay,
          selectedDeliveryDate: date,
        }),
      })

      setHasSaveDeliveryDateError(false)
      trackSaveDeliveryDate({
        newDeliverySlotId,
        order: displayedOrder,
      })
      onDeliveryDateSaved?.(date)
    } catch (error) {
      const errorMessage = `Error updating delivery date in myHome ${JSON.stringify(error)}`
      const statusCode = getStatusCode(error)

      datadogLogs.logger.error('MyHomePage update delivery date failed', {
        ...updateDeliveryDateTelemetryContext,
        updateDeliveryDateOutcome: 'error',
        statusCode,
        errorMessage,
      })
      datadogRum.addError(error, {
        ...updateDeliveryDateTelemetryContext,
        statusCode,
        errorMessage,
      })
      datadogRum.addAction('my_home_update_delivery_date_api_error', {
        ...updateDeliveryDateTelemetryContext,
        updateDeliveryDateOutcome: 'error',
        statusCode,
      })
      setHasSaveDeliveryDateError(true)
    }
  }

  return (
    <OrderTileContainer
      data-testid="order-tile"
      width={'100%'}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      borderWidth={0.5}
      borderColor={Color.ColdGrey_100}
      borderStyle={BorderStyle.Solid}
      bg={Color.White}
      style={{ overflow: 'hidden' }}
    >
      <Box paddingV={4} paddingH={4}>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
          <OrderTileHeader
            currentPrice={currentPrice}
            isManageDeliveryExpanded={isManageDeliveryExpanded}
            onManageDeliveryClick={handleManageDeliveryClick}
            order={displayedOrder}
            originalPrice={originalPrice}
            priceKind={priceKind}
          />
          <Space size={[1, 1, 1, 4]} direction="vertical" />
          <OrderTileRecipes
            newAndLimited={displayedOrder.new_and_limited}
            orderId={displayedOrder.id}
            orderStateTitle={displayedOrder.state_title}
            recipes={displayedOrder.recipes}
            topPicks={displayedOrder.top_picks}
          />
          <Space size={4} direction="vertical" />
          <OrderTileActions
            helpCta={displayedOrder.help_cta}
            isManageDeliveryExpanded={isManageDeliveryExpanded}
            isRestoreOrderDisabled={isRestoringOrder}
            onChooseRecipesClick={handleChooseRecipesClick}
            onGetHelpClick={handleGetHelpClick}
            onManageDeliveryClick={handleManageDeliveryClick}
            onRestoreOrderClick={handleRestoreOrderClick}
            orderState={displayedOrder.state}
            orderStateTitle={displayedOrder.state_title}
          />
        </Box>
      </Box>
      <ResponsiveManageDeliveryPanel
        currentOrderDeliveryDate={displayedOrder.analytics.delivery_date}
        currentOrderDeliveryDateLabel={displayedOrder.title}
        hasSaveDeliveryDateError={hasSaveDeliveryDateError}
        hasSkipOrderError={hasSkipOrderError && !isSkipDiscountModalOpen}
        isManageDeliveryExpanded={isManageDeliveryExpanded}
        isSavingDeliveryDate={isUpdating}
        isSkipBoxDisabled={isSkippingOrder || isFetchingSkipDiscount}
        isSkipOrder={isSkippedOrder}
        onSaveDeliveryDate={handleSaveDeliveryDate}
        onSkipBoxClick={handleSkipBoxRequestClick}
        onTrackBoxClick={handleTrackBoxClick}
        orderDeliveryDetails={orderDeliveryDetails}
        orderState={displayedOrder.state}
      />
      <SkipOrderDiscountModal
        discount={skipDiscount}
        hasApplyDiscountError={hasApplySkipDiscountError}
        hasSkipOrderError={hasSkipOrderError}
        isOpen={isSkipDiscountModalOpen}
        isApplyingDiscount={isApplyingSkipDiscount}
        isSkipping={isSkippingOrder}
        modalName={`my-home-skip-order-discount-modal-${displayedOrder.id}`}
        onApply={handleApplySkipDiscountClick}
        onClose={handleSkipDiscountModalClose}
        onSkip={handleConfirmSkipBoxClick}
        onView={handleSkipDiscountModalView}
      />
    </OrderTileContainer>
  )
}
